import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiInfo, FiEdit, FiTrash2 } from "react-icons/fi";
import * as route from "../../routes";
import { getSites, deleteSiteById } from "../../Services/siteService";
import { CommonTable, Loader, Button } from "../../Components/Common";
import { MESSAGES, SITE_LIST_COLUMNS, SITE_LIST_LABELS, CONFIRIM_DELETE } from "./constants";
import MobileCardView from "./Components/MobileCardView";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { UserContext } from "../../Context";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { formatDateDDMMYYYY } from "../../Utils";


export const ListSites = () => {
  const [siteList, setSiteList] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const navigate = useNavigate();
  const { isAdminOrHO } = useContext(UserContext);

  const getPaginatedSites = async (finalParams, isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setPaginationLoading(true);
    }

    try {
      const response = await getSites(finalParams);
      if (response.status === 200) {
        const newSites = response.data.results || [];
        setSiteList((prev) => (isInitial ? newSites : [...prev, ...newSites]));
        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      } else {
        setPaginationLoading(false);
      }
    }
  };

  const deleteSite = async (siteId) => {
    const confirmDelete = window.confirm(CONFIRIM_DELETE);
    if (!confirmDelete) return;

    try {
      const response = await deleteSiteById(siteId);

      if (response.status === 200 || response.status === 204) {
        toast.success(MESSAGES.DELETE_SUCCESS);
        setSiteList((prev) => prev.filter((s) => s.id !== siteId));
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.DELETE_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.DELETE_ERROR;
      toast.error(msg);
    }
  };

  const loadMoreSites = () => {
    if (pagination.offset >= pagination.count) return;
    getPaginatedSites({ offset: pagination.offset, limit: pagination.limit });
  };

  const renderFields = (key, row) => {
    switch (key) {
      case "client_name":
        return row.client?.name || "-";
      case "client_email":
        return row.client?.email || "-";
      case "client_phone":
        return row.client?.phone || "-";
      case "estimated_start_date":
        return row.estimated_start_date
          ? formatDateDDMMYYYY(row.estimated_start_date)
          : "-";
      case "estimated_end_date":
        return row.estimated_end_date
        ? formatDateDDMMYYYY(row.estimated_end_date)
        : "-";
      case "actions":
        return (
          <div className="flex gap-4 items-center">
            <div
              title="View"
              className="text-primary cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                navigate(route.VIEW_SITE.replace(":siteId", row.id));
              }}
            >
              <FiInfo size={20} />
            </div>
            {
              isAdminOrHO && (
                <div
                  title="Edit"
                  className="text-primary cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(route.EDIT_SITE.replace(":siteId", row.id));
                  }}
                >
                  <FiEdit />
                </div>
              )
            }

            {
              isAdminOrHO && (
                <div
                  title="Delete"
                  className="text-primary cursor-pointer"
                  onClick={() => deleteSite(row.id)}
                >
                  <FiTrash2 className="text-red-500 cursor-pointer hover:text-red-600"
                    title="Delete File" />
                </div>
              )
            }

          </div>
        );
      default:
        return row[key] ?? "-";
    }
  };

  useEffect(() => {
    getPaginatedSites({ offset: 0, limit: pagination.limit }, true);
  }, []);

  return (
    <div className="px-4 my-4">
      <div className="flex items-center justify-between">
        <div className="text-xl md:text-2xl text-primary font-semibold mb-3">
          Site Management
        </div>
        {
          isAdminOrHO && (<div className="flex pb-3 justify-between md:justify-end items-center gap-3">
            <Button
              className="bg-primary hover:bg-secondary text-white md:w-auto py-2 md:px-6 rounded-md shadow-md"
              onClick={() => navigate(route.ADD_SITE)}
            >
              <div className="flex items-center gap-3">Add Site</div>
            </Button>
          </div>)
        }
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block">
        <div
          className="h-[68vh] overflow-x-auto overflow-y-auto md:border md:rounded-md"
          id="scrollableDiv"
        >
          {initialLoading ? (
            <div className="flex justify-center items-center h-full py-10">
              <Loader size={4} />
            </div>
          ) : (
            <InfiniteScroll
              dataLength={siteList.length}
              next={loadMoreSites}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="scrollableDiv"
            >
              <CommonTable
                tableColumns={SITE_LIST_COLUMNS}
                tableRows={siteList}
                columnLabels={SITE_LIST_LABELS}
                renderField={renderFields}
              />
              {paginationLoading && (
                <div className="flex justify-center py-4">
                  <Loader size={3} />
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden">
        <InfiniteScroll
          dataLength={siteList.length}
          next={loadMoreSites}
          hasMore={pagination.offset < pagination.count}
          scrollableTarget="mobileScroll"
        >
          <div className="h-[70vh] overflow-y-auto" id="mobileScroll">
            <MobileCardView sites={siteList} loading={initialLoading} setSiteList={setSiteList} deleteSite={deleteSite} />
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default ListSites;